import type { BillingInfo, BillingInterval } from "@rhema/client";
import { Button, Card } from "@rhema/ui";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { formatBytes } from "../media/prepare";
import { useApi, useSession } from "../session/session";

export function bytes(n: number): string {
  return formatBytes(n).replace(/\.0 /, " ");
}

function people(n: number): string {
  return `${n.toLocaleString("en-US")} people`;
}

function money(price: { amount: number; currency: string }): string {
  const value = price.amount / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: price.currency.toUpperCase(),
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function statusLine(info: BillingInfo): string | null {
  if (info.comp) return "Your account includes this plan.";
  if (info.plan === "free") {
    return info.status === "past_due" || info.status === "canceled" || info.status === "unpaid"
      ? "Your paid plan has ended. Everything you stored is still here."
      : null;
  }
  const end = info.current_period_end ? day(info.current_period_end) : null;
  if (info.status === "past_due") {
    return "Your last payment did not go through. Update your card in Manage billing to keep this plan.";
  }
  if (info.cancel_at_period_end) {
    return end
      ? `Your plan ends on ${end}. After that you move to the Free plan, and nothing is deleted.`
      : "Your plan ends at the end of this period. Nothing is deleted.";
  }
  const every = info.interval === "year" ? "yearly" : "monthly";
  return end ? `Renews ${every} on ${end}.` : null;
}

function StorageBar({ used, limit }: { used: number; limit: number }) {
  const share = limit > 0 ? Math.min(1, used / limit) : 0;
  return (
    <div className="mt-2">
      <meter className="sr-only" aria-label="Storage used" min={0} max={limit} value={used} />
      <div aria-hidden="true" className="h-2 overflow-hidden rounded-full bg-surface-header">
        <div
          className={share >= 0.9 ? "h-full bg-scarlet" : "h-full bg-blue"}
          style={{ width: `${Math.max(share * 100, used > 0 ? 1 : 0)}%` }}
        />
      </div>
      <p className="m-0 mt-1.5 text-[12.5px] text-ink-secondary">
        {bytes(used)} of {bytes(limit)} used
      </p>
    </div>
  );
}

/**
 * Settings > Plan. The web app can upgrade and manage billing in the same tab. The desktop app
 * opens Stripe in the system browser. The iOS and Android apps only show the plan and open the
 * website, with no prices, because plans are bought on the web.
 */
export function PlanCard() {
  const api = useApi();
  const { platform, redirect } = useSession();
  const mobile = platform.kind === "capacitor";
  const location = useLocation();
  const returned = new URLSearchParams(location.search).get("billing");
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);

  const load = useCallback(
    () =>
      api
        .billing()
        .then((next) => {
          setInfo(next);
          setError(null);
          return next;
        })
        .catch((e: Error) => {
          setError(e.message);
          return null;
        }),
    [api],
  );
  useEffect(() => void load(), [load]);

  // Stripe confirms a payment with a webhook a moment after Checkout returns here.
  useEffect(() => {
    if (returned !== "success") return;
    let tries = 0;
    const timer = setInterval(() => {
      tries++;
      void load().then((next) => {
        if ((next && next.plan !== "free") || tries >= 10) clearInterval(timer);
      });
    }, 3000);
    return () => clearInterval(timer);
  }, [returned, load]);

  // The desktop app finishes in the browser, so look again when the window comes back.
  useEffect(() => {
    if (!waiting) return;
    const again = () => void load();
    window.addEventListener("focus", again);
    return () => window.removeEventListener("focus", again);
  }, [waiting, load]);

  // A "See plans" link or the return from Stripe lands on /you#plan.
  const shown = Boolean(info?.enabled);
  useEffect(() => {
    if (shown && location.hash === "#plan") document.getElementById("plan")?.scrollIntoView?.();
  }, [shown, location.hash]);

  // Hidden until paid plans are switched on, and on servers that have no plans at all.
  if (!info?.enabled) return null;

  const go = async (job: () => Promise<{ url: string }>) => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await job();
      if (platform.native) {
        await platform.openExternal(url);
        setWaiting(true);
      } else {
        redirect(url);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const upgrade = (interval: BillingInterval) => {
    const price = info.prices[interval];
    const label = interval === "month" ? "a month" : "a year";
    const text = price ? `Upgrade for ${money(price)} ${label}` : `Upgrade, paid ${interval}ly`;
    return (
      <Button
        key={interval}
        size="sm"
        variant={interval === "year" ? "tonal" : "primary"}
        disabled={busy}
        onClick={() => void go(() => api.checkout(interval))}
      >
        {text}
      </Button>
    );
  };

  const status = statusLine(info);
  const offer = info.upgrade_limits;

  return (
    <section aria-labelledby="plan-title" id="plan">
      <Card className="px-4 py-3.5">
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="plan-title" className="m-0 text-[15px] font-bold">
            Your plan
          </h2>
          <span className="font-serif text-xl font-medium">{info.plan_name}</span>
        </div>
        {status && <p className="m-0 mt-1 text-[13px] text-ink-secondary">{status}</p>}
        {returned === "success" && info.plan === "free" && (
          <p role="status" className="m-0 mt-2 text-sm font-semibold text-olive-ink">
            Thank you. Your plan changes here once Stripe confirms the payment.
          </p>
        )}

        <ul aria-label="Plan limits" className="m-0 mt-3 list-none p-0 text-sm">
          <li className="border-t border-line py-2">Groups of up to {people(info.limits.group_members)}</li>
          <li className="border-t border-line py-2">
            Voice and video calls with up to {people(info.limits.call_participants)}
          </li>
          <li className="border-t border-line py-2">
            {bytes(info.limits.storage_bytes)} for photos and files
            <StorageBar used={info.usage.storage_bytes} limit={info.limits.storage_bytes} />
          </li>
        </ul>

        {mobile ? (
          <div className="mt-3">
            <Button
              size="sm"
              variant="tonal"
              onClick={() =>
                void platform.openExternal(`${platform.serverOrigin() || window.location.origin}/you#plan`)
              }
            >
              Manage your plan on the web
            </Button>
          </div>
        ) : (
          <>
            {info.can_purchase && offer && (
              <p className="m-0 mt-3 text-[13px] text-ink-secondary">
                Individual gives you groups of up to {people(offer.group_members)}, calls with up to{" "}
                {people(offer.call_participants)} and {bytes(offer.storage_bytes)} for photos and files.
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {info.can_purchase && (["month", "year"] as const).map(upgrade)}
              {info.can_manage && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void go(() => api.billingPortal())}
                >
                  Manage billing
                </Button>
              )}
            </div>
            {waiting && (
              <p role="status" className="m-0 mt-2 text-[13px] text-ink-secondary">
                Finish in your browser. Your plan updates here when you come back.
              </p>
            )}
          </>
        )}
        {error && (
          <p role="alert" className="m-0 mt-2 text-sm text-scarlet">
            {error}
          </p>
        )}
      </Card>
    </section>
  );
}
